import React from 'react';
import {
  GitFork,
  ArrowRight,
  Users,
  GitCommit,
  GitPullRequest,
  Waves,
  FolderKanban,
  Plus,
} from 'lucide-react';
import RepoGitHubNameLink from './RepoGitHubNameLink';
import RepositorySyncIndicator from './RepositorySyncIndicator';
import { prMetaClass } from '../../components/repository/repoViewTheme';
import { cn } from '../../components/ui/uiTheme';
import { repositoryNameAndOwner } from '../../components/repositories/repositoriesPageUtils';

const pillLinkClass =
  'inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-lg border border-dark-border bg-dark-elevated px-2.5 py-1.5 text-sm text-slate-300 transition-colors hover:border-brand-purple hover:text-white';

export function buildRepositoriesConnectedColumns(navigate) {
  return [
    {
      id: 'fullName',
      accessorFn: (row) => (row.fullName || row.name || '').toLowerCase(),
      header: 'Repository',
      cell: ({ row }) => {
        const repo = row.original;
        const { name, owner, fullDisplay } = repositoryNameAndOwner(repo);
        return (
          <div className="flex items-center min-w-0">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-vivid-red/20 flex items-center justify-center mr-3">
              <GitFork className="w-5 h-5 text-vivid-red" aria-hidden />
            </div>
            <div className="min-w-0">
              <RepoGitHubNameLink htmlUrl={repo.htmlUrl} name={name} fullDisplay={fullDisplay} />
              <div className={`${prMetaClass} truncate`} title={fullDisplay}>
                {owner || '—'}
              </div>
              <RepositorySyncIndicator repo={repo} />
            </div>
          </div>
        );
      },
    },
    {
      id: 'contributorCount',
      accessorFn: (row) => row.contributorCount ?? 0,
      header: 'Contributors',
      cell: ({ row }) => (
        <span className="inline-flex items-center justify-center gap-1.5 text-sm tabular-nums text-slate-400">
          <Users className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
          {row.original.contributorCount ?? 0}
        </span>
      ),
    },
    {
      id: 'commitCount',
      accessorFn: (row) => row.commitCount ?? 0,
      header: 'Commits',
      cell: ({ row }) => (
        <span className="inline-flex items-center justify-center gap-1.5 text-sm tabular-nums text-slate-400">
          <GitCommit className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
          {row.original.commitCount ?? 0}
        </span>
      ),
    },
    {
      id: 'pullRequestCount',
      accessorFn: (row) => row.pullRequestCount ?? 0,
      header: 'PR',
      cell: ({ row }) => (
        <span className="inline-flex items-center justify-center gap-1.5 text-sm tabular-nums text-slate-400">
          <GitPullRequest className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
          {row.original.pullRequestCount ?? 0}
        </span>
      ),
    },
    {
      id: 'streamName',
      accessorFn: (row) => (row.streamName || '').toLowerCase(),
      header: 'Stream',
      cell: ({ row }) => {
        const repo = row.original;
        if (!repo.streamId || !repo.streamName) {
          return <span className="text-sm text-slate-500">—</span>;
        }
        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/streams/${repo.streamId}`);
            }}
            className={pillLinkClass}
            aria-label={`Open stream ${repo.streamName}`}
          >
            <Waves className="h-4 w-4 shrink-0 text-vivid-red" aria-hidden />
            <span className="truncate">{repo.streamName}</span>
          </button>
        );
      },
    },
    {
      id: 'projectName',
      accessorFn: (row) => (row.projectName || '').toLowerCase(),
      header: 'Project',
      cell: ({ row }) => {
        const repo = row.original;
        if (!repo.streamId || !repo.projectId || !repo.projectName) {
          return <span className="text-sm text-slate-500">—</span>;
        }
        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/streams/${repo.streamId}/projects/${repo.projectId}`);
            }}
            className={pillLinkClass}
            aria-label={`Open project ${repo.projectName}`}
          >
            <FolderKanban className="h-4 w-4 shrink-0 text-vivid-purple" aria-hidden />
            <span className="truncate">{repo.projectName}</span>
          </button>
        );
      },
    },
    {
      id: 'openRepo',
      header: '',
      enableSorting: false,
      cell: () => <ArrowRight className="w-5 h-5 text-slate-500 flex-shrink-0" aria-hidden />,
    },
  ];
}

export function buildRepositoriesDiscoveredColumns(handleOpenAssign) {
  return [
    {
      id: 'fullName',
      accessorFn: (row) => (row.fullName || '').toLowerCase(),
      header: 'Repository',
      cell: ({ row }) => {
        const repo = row.original;
        const { name, owner, fullDisplay } = repositoryNameAndOwner(repo);
        return (
          <div className="flex items-center min-w-0">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-vivid-red/20 flex items-center justify-center mr-3">
              <GitFork className="w-5 h-5 text-vivid-red" aria-hidden />
            </div>
            <div className="min-w-0">
              <RepoGitHubNameLink htmlUrl={repo.htmlUrl} name={name} fullDisplay={fullDisplay} />
              <div className={`${prMetaClass} truncate`} title={fullDisplay}>
                {owner || '—'}
              </div>
            </div>
          </div>
        );
      },
    },
    {
      id: 'description',
      accessorFn: (row) => (row.description || '').toLowerCase(),
      header: 'Description',
      enableSorting: false,
      cell: ({ row }) => {
        const repo = row.original;
        if (repo.description) {
          return (
            <span className="text-sm text-slate-400 line-clamp-2 max-w-md" title={repo.description}>
              {repo.description}
            </span>
          );
        }
        const bits = [repo.language, repo.isPrivate && 'Private'].filter(Boolean);
        return bits.length ? (
          <span className="text-sm text-slate-400 truncate">{bits.join(' · ')}</span>
        ) : (
          <span className="text-sm text-slate-500">—</span>
        );
      },
    },
    {
      accessorKey: 'language',
      header: 'Language',
      cell: ({ row }) => (
        <span className="text-sm text-slate-400">{row.original.language || '—'}</span>
      ),
    },
    {
      id: 'visibility',
      accessorFn: (row) => (row.isPrivate ? 'private' : 'public'),
      header: 'Visibility',
      cell: ({ row }) => (
        <span className="text-sm text-slate-400">
          {row.original.isPrivate ? 'Private' : 'Public'}
        </span>
      ),
    },
    {
      id: 'assign',
      header: '',
      enableSorting: false,
      cell: ({ row }) => {
        const fn = row.original.fullName || row.original.name || 'repository';
        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenAssign(row.original);
            }}
            className={cn(
              'btn-secondary-mdl-sm inline-flex items-center gap-1.5 flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple/50'
            )}
            aria-label={`Connect ${fn} to project`}
            title="Connect to project"
          >
            <Plus className="h-4 w-4 shrink-0" aria-hidden />
            Connect to project
          </button>
        );
      },
    },
  ];
}
